import * as R from 'ramda';
import { call, fork, put, takeEvery } from 'redux-saga/effects';

import actionTypes from '@atom/actions/actionTypes';
import * as formInstanceActions from '@atom/actions/formInstanceActions';
import * as inventoryAssetActions from '@atom/actions/inventoryAssetActions';
import textConstants from '@atom/constants/textConstants';
import { Snackbar } from '@atom/mui';
import accountUtilities from '@atom/utilities/accountUtilities';
import api from '@atom/utilities/api';
import {
  INVENTORY_ASSETS_ENDPOINT,
  INVENTORY_ASSETS_FILTER_ENDPOINT,
} from '@atom/utilities/endpoints';
import { TOTAL_COUNT_HEADER } from '@atom/utilities/requestUtilities';

// "Cost" and "Quantity on Hand" cells show the budget unit
// "Quantity on Hand" needs the conversion units to show default unit
export const getRequestFields = (columns: { fieldName: string }[]): string => {
  const baseFields = columns.map(field => field.fieldName);
  const showsRate = baseFields.includes('budget.rate');
  const showsQuantityOnHand = baseFields.includes('budget.quantityOnHand');

  return R.uniq([
    ...baseFields,
    ...(showsRate || showsQuantityOnHand ? ['budget.unit'] : []),
    ...(showsQuantityOnHand ? ['budget.conversionUnits'] : []),
  ]).join(',');
};

export function* getInventoryAssets({ data: request }) {
  try {
    const {
      parentCategoryId,
      columns,
      page,
      limit,
      sortBy,
      isAscending,
      includeInactive,
    } = request;

    const fields = getRequestFields(columns);

    const params = {
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
      ...(fields ? { fields } : {}),
      ...(sortBy ? { sortBy } : {}),
      ...(!R.isNil(isAscending) ? { isAscending } : {}),
      ...(parentCategoryId ? { parentCategoryId } : {}),
      ...(!R.isNil(includeInactive) ? { includeInactive } : {}),
    };

    const { data, headers } = yield call(
      api.get,
      INVENTORY_ASSETS_ENDPOINT,
      params,
    );
    const total = Number(headers[TOTAL_COUNT_HEADER]) || 0;

    yield put(
      inventoryAssetActions.getInventoryAssetsSuccess({
        parentCategoryId,
        columns,
        data,
        total,
      }),
    );
  } catch (error) {
    yield fork(
      accountUtilities.apiErrorHandler,
      error,
      inventoryAssetActions.getInventoryAssetsFailure,
    );
  }
}

export function* getInventoryAssetsFilter({ data: request }) {
  try {
    const {
      rootAssets,
      schemaId,
      categoryIds,
      columns,
      sortBy,
      isAscending,
      body,
      page,
      limit,
      hasPendingChangesOnly,
      includeInactive,
    } = request;

    const fields = getRequestFields(columns);

    const params = {
      ...(schemaId ? { schemaId } : {}),
      ...(!R.isNil(rootAssets) ? { rootAssets } : {}),
      ...(categoryIds ? { categoryIds: categoryIds.join(',') } : {}),
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
      ...(fields ? { fields } : {}),
      ...(sortBy ? { sortBy } : {}),
      ...(!R.isNil(isAscending) ? { isAscending } : {}),
      ...(!R.isNil(hasPendingChangesOnly) ? { hasPendingChangesOnly } : {}),
      ...(!R.isNil(includeInactive) ? { includeInactive } : {}),
    };

    const { data, headers } = yield call(
      api.post,
      INVENTORY_ASSETS_FILTER_ENDPOINT,
      body,
      params,
    );
    const total = Number(headers[TOTAL_COUNT_HEADER]) || 0;

    yield put(
      inventoryAssetActions.getInventoryAssetsFilterSuccess({
        columns,
        data,
        total,
      }),
    );
  } catch (error) {
    yield fork(
      accountUtilities.apiErrorHandler,
      error,
      inventoryAssetActions.getInventoryAssetsFilterFailure,
    );
  }
}

export function* inventoryRootAssetBatchCreateFHWA({ data: batchPayload }) {
  try {
    const endpoint = `${INVENTORY_ASSETS_ENDPOINT}/${batchPayload.assetId}/elements`;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { assetId, formInstanceId, ...requestPayload } = batchPayload;

    yield call(api.post, endpoint, requestPayload);

    yield put(inventoryAssetActions.createRootAssetBatchFHWASuccess());

    yield put(
      formInstanceActions.retrieveFormInstance({
        formInstanceId: batchPayload.formInstanceId,
      }),
    );
  } catch (error) {
    yield fork(
      accountUtilities.apiErrorHandler,
      error,
      inventoryAssetActions.createRootAssetBatchFHWAFailure,
    );
  }
}

export function* deleteFHWAElement({ data: { assetId, formInstanceId } }) {
  try {
    const endpoint = `${INVENTORY_ASSETS_ENDPOINT}/${assetId}`;

    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_DELETING_TEXT,
    });

    yield call(api.delete, endpoint);

    yield put(inventoryAssetActions.deleteFhwaElementSuccess());

    Snackbar.info({
      message: 'Element Deleted',
    });

    yield put(
      formInstanceActions.retrieveFormInstance({
        formInstanceId,
      }),
    );
  } catch (error) {
    yield fork(
      accountUtilities.apiErrorHandler,
      error,
      inventoryAssetActions.deleteFhwaElementFailure,
    );
  }
}

export function* deleteRootInventoryAsset(action) {
  try {
    const endpoint = `${INVENTORY_ASSETS_ENDPOINT}/${action.data}`;

    Snackbar.info({
      message: textConstants.GENERIC_APPLICATION_DELETING_TEXT,
    });

    yield call(api.delete, endpoint);

    yield put(
      inventoryAssetActions.rootInventoryAssetDeletionSuccess(action.data),
    );

    Snackbar.info({
      message: 'Root Asset Deleted',
    });
  } catch (error) {
    if (error.response && error.response.status === 422) {
      Snackbar.error({
        message:
          'Work is currently associated with this inventory asset. Deletion is not allowed.',
      });
    }

    yield fork(
      accountUtilities.apiErrorHandler,
      error,
      inventoryAssetActions.rootInventoryAssetDeletionFailure,
    );
  }
}

/* WATCHERS */
export function* retrieveInventoryAssets() {
  // @ts-ignore
  yield takeEvery(actionTypes.REQUEST_INVENTORY_ASSETS, getInventoryAssets);
}

export function* retrieveInventoryAssetsFilter() {
  yield takeEvery(
    // @ts-ignore
    actionTypes.REQUEST_INVENTORY_ASSETS_FILTER,
    getInventoryAssetsFilter,
  );
}

export function* requestRootAssetBatchCreateFHWA() {
  yield takeEvery(
    // @ts-ignore
    actionTypes.REQUEST_ROOT_ASSET_BATCH_CREATE_FHWA,
    inventoryRootAssetBatchCreateFHWA,
  );
}

export function* requestFHWAElementDeletion() {
  // @ts-ignore
  yield takeEvery(actionTypes.REQUEST_FHWA_ELEMENT_DELETE, deleteFHWAElement);
}

export function* requestRootInventoryAssetDeletion() {
  yield takeEvery(
    actionTypes.REQUEST_ROOT_INVENTORY_ASSET_DELETION,
    deleteRootInventoryAsset,
  );
}

export default [
  retrieveInventoryAssets,
  retrieveInventoryAssetsFilter,
  requestRootAssetBatchCreateFHWA,
  requestRootInventoryAssetDeletion,
  requestFHWAElementDeletion,
];
